import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IColumnDef, ICommonDropdownResponse } from '../common-table.model';
import { SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { JwtService } from '../../../auth/jwtservice';
import { firstValueFrom } from 'rxjs';
import { CommonTableService } from '../common-table.service';
import { CommonService } from '../../../services/common';

/**
 * CommonTableDarkComponent
 * Dark-themed variant of the common table component
 * Uses shared business logic from CommonTableService
 * Styled for dark backgrounds and layouts
 */
@Component({
    selector: 'app-common-table-dark',
    templateUrl: './common-table-dark.component.html',
    styleUrl: './common-table-dark.component.scss'
})
export class CommonTableDarkComponent implements OnInit, OnChanges {

    @Input({ required: true }) columnDefs: IColumnDef[] = [];
    @Input({ required: true }) data: any[] = [];
    @Output() commonDropdownChange = new EventEmitter<ICommonDropdownResponse>();
    @Output() changeModelStatus = new EventEmitter<any>();
    @Output() selectedRowsChange: EventEmitter<any[]> = new EventEmitter();
    @Output() hyperlinkClicked = new EventEmitter<any>();
    @Output() rowAction = new EventEmitter<{ action: any; row: any }>();

    @Input() showSearchBar: boolean = true;
    @Input() enableRowStyles: boolean = false;
    @Input() rowStyles!: any[];

    @ViewChild('dt') dt: Table | undefined;

    allSelected: boolean = false;
    filteredData!: any[];
    searchTerm: string = '';
    selectedAction: any;
    fields: string[] = [];
    dataCopy: any[] = [];
    selectedItem: any;
    selectedRows: any[] = [];
    viewedContactNumber: any;
    viewContactModal: boolean = false;
    currentUserContactNumber: any;
    roleListData: any;
    showContactColumn: boolean = false;
    showCallIcon: boolean = false;

    constructor(
        private jwtService: JwtService,
        private commonTableService: CommonTableService,
        private commonService: CommonService
    ) { }

    async ngOnInit(): Promise<void> {
        this.roleListData = await firstValueFrom(this.jwtService.currentUserRole$());
        this.filteredData = this.dataCopy;
        this.fields = [];

        if (this.columnDefs && Array.isArray(this.columnDefs)) {
            this.columnDefs.forEach((value) => {
                this.fields.push(value.field);
            });
        }

        if (this.roleListData?.includes('View_Client_Contacts')) {
            this.showContactColumn = true;
        } else {
            this.showContactColumn = false;
        }

        if (this.roleListData?.includes('Can_Call_Client')) {
            this.showCallIcon = true;
        } else {
            this.showCallIcon = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.dataCopy = [];
        if (changes['data'] && changes['data'].currentValue?.length > 0) {
            const updatedData = changes['data']?.currentValue?.map((row: any) => {
                this.fields.forEach(element => {
                    if (row[element] && typeof row[element] !== 'object' && !Array.isArray(row[element])) {
                        row[`${element}_Copy`] = row[element].toString().replace(/[^a-zA-Z0-9]/gi, '');

                        this.columnDefs.push({
                            columnType: 'text',
                            field: `${element}_Copy`,
                            header: `${element}_Copy`
                        });
                    }
                });
                return row;
            });
            this.dataCopy = updatedData;
        }
    }

    /**
     * Get row style using shared service
     */
    getRowStyle(rowId?: number): any {
        return this.commonTableService.getRowStyle(rowId, this.rowStyles, this.enableRowStyles);
    }

    /**
     * Filter table data using shared service
     */
    filterData($event: any, filterType: string = 'contains'): void {
        this.commonTableService.filterData(this.dt, ($event.target as HTMLInputElement).value, filterType);
    }

    /**
     * Handle dropdown click using shared service
     */
    onDropdownClick(event: any, selectedRow: any, options: any[]): void {
        const result = this.commonTableService.onDropdownClick(event, selectedRow, options);

        // If the new value is the same as the previous one, force the update
        if (this.selectedAction === event.value) {
            this.commonDropdownChange.emit(result);
            this.selectedAction = '';
        } else {
            this.selectedAction = event.value;
        }

        this.commonDropdownChange.emit(result);
        this.selectedAction = '';
    }

    /**
     * Handle image click
     */
    onImageClick(event: any, selectedRow: any): void {
        this.changeModelStatus.emit({
            'currentRow': selectedRow,
        });
    }

    /**
     * Toggle select all using shared service
     */
    toggleSelectAll(event: any): void {
        const checked = event?.checked;
        this.allSelected = checked;
        this.dataCopy = this.commonTableService.toggleSelectAll(this.dataCopy, checked);
        this.selectedRowsChange.emit(this.dataCopy);
    }

    /**
     * Update select all state using shared service
     */
    updateSelectAll(): void {
        this.allSelected = this.commonTableService.updateSelectAll(this.dataCopy);
    }

    /**
     * Handle checkbox change using shared service
     */
    onCheckboxChange(event: any, currentRow: any): void {
        this.selectedRows = this.commonTableService.onCheckboxChange(this.dataCopy, currentRow, event?.checked);
        this.selectedRowsChange.emit(this.selectedRows);
    }

    /**
     * Handle radio change using shared service
     */
    onRadioChange(event: any, currentRow: any): void {
        currentRow.selection = true;
        this.selectedRows = currentRow;
        this.selectedRowsChange.emit(this.selectedRows);
    }

    /**
     * Get options for dropdown
     */
    getOptions(columnDef: any): SelectItem[] {
        return columnDef.options || [];
    }

    /**
     * Truncate text using shared service
     */
    setTextLength(data: string, textLength?: number): string {
        return this.commonTableService.setTextLength(data, textLength);
    }

    /**
     * Handle icon click
     */
    onIconClick(action: any, row: any): void {
        this.rowAction.emit({ action, row });
    }

    /**
     * Filter dropdown options using shared service
     */
    filterOptions(options: any, selectedValue: any, currentRecord: any): any[] {
        return this.commonTableService.filterOptions(options, selectedValue, currentRecord);
    }

    /**
     * Handle eye icon click on contact column
     */
    onClickEyeOfContactColumn(cellData: any): void {
        const result = this.commonTableService.onClickEyeOfContactColumn(cellData);
        this.viewContactModal = result.viewContactModal;
        this.viewedContactNumber = result.viewedContactNumber;
        this.currentUserContactNumber = this.jwtService.getUserPropertyValueFromToken('contact').replace(/(\d{5})(\d{5})/, '$1-$2');
    }

    /**
     * Handle call click
     */
    async clickToCall(event: any): Promise<void> {
        event.preventDefault();
        await this.commonTableService.clickToCall(this.viewedContactNumber);
    }

    /**
     * Handle hyperlink click
     */
    handleHyperlinkClick(event: MouseEvent, col: IColumnDef, currentRow: any): void {
        this.commonTableService.handleHyperlinkClick(event, currentRow, col.onClick);
        this.hyperlinkClicked.emit(currentRow);
    }

    /**
     * Get badge configuration for status display
     */
    getBadgeConfig(column: IColumnDef, row: any): { color: string; icon: string; label: string } {
        const statusValue = row[column.badgeConfig?.statusField || column.field]?.toLowerCase();
        const colorMap = column.badgeConfig?.colorMap || {};
        const iconMap = column.badgeConfig?.iconMap || {};
        const labelMap = column.badgeConfig?.labelMap || {};

        return {
            color: colorMap[statusValue] || 'warning',
            icon: iconMap[statusValue] || 'fa-circle',
            label: labelMap[statusValue] || statusValue || 'Unknown'
        };
    }

    /**
     * Get file icon based on file extension
     * Uses CommonService for universal icon mapping across the app
     */
    getFileIcon(fileExtn: string): string {
        return this.commonService.getFileIcon(fileExtn);
    }

    /**
     * Safely get tooltip text from column definition and current row
     * Returns empty string if toillfieldValue is null, undefined, or empty
     * Prevents "undefined" from appearing in tooltips
     */
    getTooltipText(col: IColumnDef, currentRow: any): string {
        if (col?.tooltipField) {
            const value = currentRow[col?.tooltipField];
            if (value !== null && value !== undefined && value !== '') {
                return String(value);
            }
            return '';
        }

        const value = currentRow[col?.field];
        if (value !== null && value !== undefined && value !== '') {
            return String(value);
        }
        return '';
    }
}

export { IColumnDef };
